import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Wine } from 'lucide-react';
import { EventBranding } from '@/types';

interface TastingLayoutProps {
  children: ReactNode;
  showBranding?: boolean;
  branding?: EventBranding | null;
  showProgress?: boolean;
  progress?: number;
}

export default function TastingLayout({
  children,
  showBranding = false,
  branding,
  showProgress = false,
  progress = 0,
}: TastingLayoutProps) {
  return (
    <div className="min-h-screen bg-dark-950 flex flex-col">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-dark-800/50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            {showBranding && branding?.logo_url ? (
              <img
                src={branding.logo_url}
                alt="Logo"
                className="h-8 w-auto"
              />
            ) : (
              <>
                <Wine className="w-6 h-6 text-primary-500" />
                <span className="font-display font-semibold text-dark-100">
                  SpiritVault
                </span>
              </>
            )}
          </div>

          {/* Progress indicator */}
          {showProgress && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-dark-400">{Math.round(progress)}%</span>
              <div className="w-24 h-2 bg-dark-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-primary-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-dark-800/50 py-4">
        <div className="max-w-4xl mx-auto px-4 text-center text-dark-500 text-sm">
          <p>
            Powered by{' '}
            <a
              href="https://spiritvault.pro"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-400 hover:text-primary-300"
            >
              SpiritVault
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
